import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  PencilIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  ShieldCheckIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';
import '../../styles/components.css';
import { useAuth } from '../../context/AuthContext';
import UserHistory from './UserHistory';
import { formatDate } from '../../utils/dateUtils';
import { useConcessionTypes, poolDisplayName } from '../../utils/concessions';

const ManageUsers = () => {
  const { user: currentUser, isAdmin, startImpersonation } = useAuth();
  const { types: concessionTypes } = useConcessionTypes(true);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(''); // Immediate input value for smooth typing
  const [searchTerm, setSearchTerm] = useState(''); // Debounced search term for API calls
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddToClassModal, setShowAddToClassModal] = useState(false);
  const [showRemoveFromClassModal, setShowRemoveFromClassModal] = useState(false);
  const [selectedUserForClass, setSelectedUserForClass] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [userBookings, setUserBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [overrideConcessionRefund, setOverrideConcessionRefund] = useState(false);
  const [showUserHistoryModal, setShowUserHistoryModal] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    sendEmail: false
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  // Concession grant modal (per-type top-up after payment confirmed)
  const [concessionAdjustments, setConcessionAdjustments] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [addForm, setAddForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'user',
    concessions: 0,
    concessionTypes: [],
    notes: ''
  });
  // Initial per-wallet amounts for a brand-new user (key -> string)
  const [addConcessionAmounts, setAddConcessionAmounts] = useState({});

  const toggleAddFormMark = (key) => {
    setAddForm((prev) => {
      const current = Array.isArray(prev.concessionTypes) ? prev.concessionTypes : [];
      return {
        ...prev,
        concessionTypes: current.includes(key)
          ? current.filter(k => k !== key)
          : [...current, key]
      };
    });
    // Drop any typed initial amount when the wallet is unticked
    setAddConcessionAmounts((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  // Track if this is the initial load
  const isInitialLoadRef = useRef(true);
  const previousPageRef = useRef(1);

  const fetchUsers = useCallback(async (page, search = searchTerm, status = filterStatus) => {
    try {
      // Only show loading spinner on initial load or page changes, not during search
      const isInitialLoad = isInitialLoadRef.current;
      const isPageChange = page !== previousPageRef.current;
      
      if (isInitialLoad || isPageChange) {
        setLoading(true);
      }
      
      // Mark that initial load is complete
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
      previousPageRef.current = page;
      
      // Ensure the token is set in axios headers
      const token = localStorage.getItem('token');
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        search: search || '',
        status: status === 'all' ? '' : status
      });
      
      const response = await axios.get(`/api/admin/users?${params}`);
      
      setUsers(response.data.users || []);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(parseInt(response.data.currentPage) || page);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Failed to fetch users: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus, pageSize]);

  // Debounce search input to avoid excessive API calls
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      // Update the actual search term after user stops typing
      setSearchTerm(searchInput);
      setCurrentPage(1); // Reset to first page when searching
    }, 400); // 400ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchInput]); // Only depend on searchInput, not searchTerm

  useEffect(() => {
    // Only fetch users if user is authenticated and is admin
    if (currentUser && isAdmin) {
      fetchUsers(currentPage, searchTerm, filterStatus);
    } else if (currentUser && !isAdmin) {
      setLoading(false);
    }
  }, [currentUser, isAdmin, currentPage, searchTerm, filterStatus, fetchUsers]);

  // Handle search input changes (immediate update for smooth typing)
  const handleSearchChange = (newSearchTerm) => {
    setSearchInput(newSearchTerm); // Update input immediately for smooth UX
    // The debounced useEffect will update searchTerm after user stops typing
  };

  // Handle filter changes
  const handleFilterChange = (newFilterStatus) => {
    setFilterStatus(newFilterStatus);
    setCurrentPage(1); // Reset to first page when filtering
    // The useEffect will automatically fetch when filterStatus or currentPage changes
  };

  // Handle page changes
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setConcessionAdjustments({});
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'user',
      concessions: user.concessions || 0,
      concessionTypes: Array.isArray(user.concessionTypes) ? [...user.concessionTypes] : [],
      notes: user.notes && user.notes.length > 0 ? user.notes[0].content : '',
      isActive: user.isActive !== false
    });
    setShowEditModal(true);
    setDropdownOpen(null);
  };

  const toggleUserMark = (key) => {
    setEditForm((prev) => {
      const current = Array.isArray(prev.concessionTypes) ? prev.concessionTypes : [];
      return {
        ...prev,
        concessionTypes: current.includes(key)
          ? current.filter(k => k !== key)
          : [...current, key]
      };
    });
  };

  const handleAddToClass = async (user) => {
    setSelectedUserForClass(user);
    setDropdownOpen(null);
    
    try {
      // Fetch all upcoming classes (including full ones)
      const response = await axios.get('/api/classes');
      const classes = response.data?.classes || [];
      
      // Filter for upcoming classes (both available and full)
      const userTags = Array.isArray(user.concessionTypes)
        ? user.concessionTypes.map(t => String(t).trim().toLowerCase())
        : [];

      const upcomingClasses = classes.filter(classItem => {
        const classStartTime = new Date(classItem.startTime);
        const now = new Date();
        if (!(classStartTime > now)) return false;

        // Only show classes matching the user's actual tag. A class without a
        // concession type draws from the shared pool and is available to
        // everyone; a tagged class is only shown when the user is enrolled in
        // that same tag (e.g. "50+", "Yoga").
        const classTag = classItem.concessionType
          ? String(classItem.concessionType).trim().toLowerCase()
          : null;

        return !classTag || userTags.includes(classTag);
      });
      
      setAvailableClasses(upcomingClasses);
      setShowAddToClassModal(true);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Failed to fetch available classes');
    }
  };

  const updateButtonText = () => {
    const classSelect = document.getElementById('classSelect');
    const button = document.getElementById('addToClassButton');
    
    if (classSelect && button) {
      const selectedClass = availableClasses.find(cls => cls._id === classSelect.value);
      if (selectedClass) {
        button.textContent = selectedClass.isFull ? 'Add to Waitlist' : 'Add to Class';
      } else {
        button.textContent = 'Add to Class';
      }
    }
  };

  const handleAdminBookUser = async () => {
    const classSelect = document.getElementById('classSelect');
    const overrideConcessions = document.getElementById('overrideConcessions');
    
    if (!classSelect.value) {
      toast.error('Please select a class');
      return;
    }

    // Find the selected class to check if it's full
    const selectedClass = availableClasses.find(cls => cls._id === classSelect.value);
    const isClassFull = selectedClass?.isFull;

    try {
      if (isClassFull) {
        // Add to waitlist if class is full
        const response = await axios.post(`/bookings/waitlist/${classSelect.value}`, {
          adminOverride: true,
          overrideConcessions: overrideConcessions.checked,
          userId: selectedUserForClass._id
        });

        if (response.data.success) {
          toast.success(`Successfully added ${selectedUserForClass.firstName} ${selectedUserForClass.lastName} to the waitlist for ${selectedClass.name}!`);
        } else {
          toast.error(response.data.message || 'Failed to add user to waitlist');
        }
      } else {
        // Add to class if not full
      const response = await axios.post('/api/admin/bookings/manual', {
        userId: selectedUserForClass._id,
        classId: classSelect.value,
        overrideConcessions: overrideConcessions.checked
      });

      if (response.data.success) {
        toast.success(`Successfully added ${selectedUserForClass.firstName} ${selectedUserForClass.lastName} to the class!`);
        } else {
          toast.error(response.data.message || 'Failed to add user to class');
        }
      }

      // Close modal and refresh data
        setShowAddToClassModal(false);
        setSelectedUserForClass(null);
        setAvailableClasses([]);
        // Refresh users data
        fetchUsers(currentPage, searchTerm, filterStatus);
    } catch (error) {
      console.error('Error adding user to class/waitlist:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to add user to class/waitlist');
      }
    }
  };

  const handleRemoveFromClass = async (user) => {
    setSelectedUserForClass(user);
    setDropdownOpen(null);
    
    try {
      // Fetch user's current bookings
      const response = await axios.get(`/api/admin/users/${user._id}/bookings`);
      const bookings = response.data?.bookings || [];
      
      // Filter for confirmed bookings (upcoming classes)
      const confirmedBookings = bookings.filter(booking => 
        booking.status === 'confirmed' && 
        new Date(booking.displayStartTime || booking.classId.startTime) > new Date()
      );
      
      setUserBookings(confirmedBookings);
      setShowRemoveFromClassModal(true);
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      toast.error('Failed to fetch user bookings');
    }
  };

  const handleAdminRemoveUser = async () => {
    if (selectedBookings.length === 0) {
      toast.error('Please select at least one class to remove from');
      return;
    }

    const classCount = selectedBookings.length;
    const classText = classCount === 1 ? 'class' : 'classes';
    
    if (!window.confirm(`Are you sure you want to remove ${selectedUserForClass.firstName} ${selectedUserForClass.lastName} from ${classCount} ${classText}?`)) {
      return;
    }

    try {
      // Remove from multiple classes
      const removalPromises = selectedBookings.map(bookingId => 
        axios.delete(`/api/admin/bookings/manual/${bookingId}`, {
          data: { overrideConcessionRefund }
        })
      );
      
      const results = await Promise.allSettled(removalPromises);
      
      // Check results
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.data.success);
      const failed = results.filter(result => result.status === 'rejected' || !result.value.data.success);
      
      if (successful.length === classCount) {
        toast.success(`Successfully removed ${selectedUserForClass.firstName} ${selectedUserForClass.lastName} from ${classCount} ${classText}!`);
      } else if (successful.length > 0) {
        toast.warning(`Removed from ${successful.length} of ${classCount} ${classText}. ${failed.length} failed.`);
      } else {
        toast.error('Failed to remove user from any classes');
        return;
      }
      
      // Close modal and reset state
        setShowRemoveFromClassModal(false);
        setSelectedUserForClass(null);
        setUserBookings([]);
      setSelectedBookings([]);
        // Refresh users data
        fetchUsers(currentPage, searchTerm, filterStatus);
    } catch (error) {
      console.error('Error removing user from classes:', error);
      toast.error('Failed to remove user from classes');
    }
  };


  const handleViewUserHistory = (user) => {
    setSelectedUserForHistory(user);
    setShowUserHistoryModal(true);
    setDropdownOpen(null);
  };

  const handleResetPassword = (user) => {
    setSelectedUserForReset(user);
    setResetPasswordForm({
      newPassword: '',
      confirmPassword: '',
      sendEmail: false
    });
    setShowResetPasswordModal(true);
    setDropdownOpen(null);
  };

  const handleDeleteUser = (user) => {
    setSelectedUserForDelete(user);
    setShowDeleteModal(true);
    setDropdownOpen(null);
  };

  const handleLoginAsUser = async (user) => {
    setDropdownOpen(null);
    const result = await startImpersonation(user._id);
    if (result.success) {
      // Redirect to dashboard as the impersonated user
      window.location.href = '/dashboard';
    }
  };

  const handleResendVerification = async (user) => {
    try {
      const response = await axios.post(`/api/admin/users/${user._id}/resend-verification`);
      
      if (response.data.success) {
        toast.success('Verification link sent successfully');
      } else {
        toast.error(response.data.message || 'Failed to send verification link');
      }
    } catch (error) {
      console.error('Error resending verification link:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send verification link';
      toast.error(errorMessage);
    } finally {
      setDropdownOpen(null);
    }
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordForm.newPassword || !resetPasswordForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetPasswordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await axios.put(`/api/admin/users/${selectedUserForReset._id}/reset-password`, {
        newPassword: resetPasswordForm.newPassword,
        sendEmail: resetPasswordForm.sendEmail
      });

      if (response.data.success) {
        toast.success(`Password reset successfully${resetPasswordForm.sendEmail ? ' and email sent' : ''}`);
        setShowResetPasswordModal(false);
        setSelectedUserForReset(null);
        setResetPasswordForm({
          newPassword: '',
          confirmPassword: '',
          sendEmail: false
        });
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleDeleteUserSubmit = async () => {
    try {
      const response = await axios.delete(`/api/admin/users/${selectedUserForDelete._id}`);
      
      if (response.data.success) {
        toast.success('User deleted successfully');
        setShowDeleteModal(false);
        setSelectedUserForDelete(null);
        fetchUsers(currentPage, searchTerm, filterStatus); // Refresh the users list
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const calculateDropdownPosition = (buttonElement) => {
    if (!buttonElement) return { top: '100%', bottom: 'auto', left: '0' };
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    // Measured menu height: up to 7 items (~40px each) + padding
    const dropdownHeight = 320;
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
      // Position below
      return { 
        top: `${rect.bottom + 4}px`, 
        bottom: 'auto',
        left: `${rect.right - 180}px` // Align right edge of dropdown with right edge of button
      };
    } else {
      // Position above
      return { 
        top: 'auto', 
        bottom: `${viewportHeight - rect.top + 4}px`,
        left: `${rect.right - 180}px` // Align right edge of dropdown with right edge of button
      };
    }
  };

  const handleDropdownToggle = (userId, buttonElement) => {
    if (dropdownOpen === userId) {
      setDropdownOpen(null);
    } else {
      const position = calculateDropdownPosition(buttonElement);
      setDropdownPosition({ [userId]: position });
      setDropdownOpen(userId);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    setIsSavingEdit(true);
    try {
      // Validate required fields
      if (!editForm.firstName || !editForm.lastName || !editForm.email) {
        toast.error('Please fill in all required fields: First Name, Last Name, and Email');
        return;
      }

      // Ensure the token is set in axios headers
      const token = localStorage.getItem('token');
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      // 1. Save profile changes first (incl. marks) so grant validation sees them.
      //    `concessions` is deliberately omitted - per-pool balances are adjusted
      //    via the grant endpoint below. Exception: demotion to coach zeroes it.
      const payload = { ...editForm };
      delete payload.concessions;
      if (editForm.role === 'coach' && selectedUser.role !== 'coach') {
        payload.concessions = 0;
      }
      const response = await axios.put(`/api/admin/users/${selectedUser._id}`, payload);

      // 2. Apply per-pool adjustments through the grant endpoint (audit + floor).
      //    Only for purses still marked after the profile save above.
      const currentMarks = Array.isArray(editForm.concessionTypes) ? editForm.concessionTypes : [];
      const adjustments = Object.entries(concessionAdjustments)
        .map(([poolKey, raw]) => ({ poolKey, amount: parseInt(raw, 10) }))
        .filter(({ amount }) => Number.isFinite(amount) && amount !== 0)
        .filter(({ poolKey }) => currentMarks.includes(poolKey));

      for (const { poolKey, amount } of adjustments) {
        await axios.post(`/api/admin/users/${selectedUser._id}/concessions/grant`, {
          concessionType: poolKey,
          amount,
          reason: 'Admin adjustment'
        });
      }

      if (response.status === 200) {
        toast.success('User updated successfully');
        setShowEditModal(false);
        fetchUsers(currentPage, searchTerm, filterStatus);
      } else {
        toast.error('Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleAddUser = async () => {
    try {
      const response = await axios.post('/api/admin/users', addForm);

      if (response.status === 201) {
        // Apply any initial wallet amounts to the new user (grants only work
        // for marked types, which the modal enforces).
        const grants = Object.entries(addConcessionAmounts)
          .map(([poolKey, raw]) => ({ poolKey, amount: parseFloat(raw) }))
          .filter(({ amount }) => Number.isFinite(amount) && amount !== 0);

        for (const { poolKey, amount } of grants) {
          await axios.post(`/api/admin/users/${response.data.user.id}/concessions/grant`, {
            concessionType: poolKey,
            amount,
            reason: 'Initial balance at user creation'
          });
        }

        toast.success('User created successfully');
        setShowAddModal(false);
        setAddConcessionAmounts({});
        setAddForm({
          email: '',
          firstName: '',
          lastName: '',
          phone: '',
          role: 'user',
          concessions: 0,
          concessionTypes: [],
          notes: ''
        });
        fetchUsers(currentPage, searchTerm, filterStatus);
      } else {
        toast.error('Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create user';
      toast.error(errorMessage);
    }
  };

  // Since we're doing server-side filtering, we don't need client-side filtering
  // The users array already contains the filtered results from the server
  
  // Sort users (server-side sorting would be better, but keeping this for now)
  const sortedUsers = [...users].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'concessions':
        aValue = a.concessions || 0;
        bValue = b.concessions || 0;
        break;
      case 'totalClassesAttended':
        aValue = a.totalClassesAttended || 0;
        bValue = b.totalClassesAttended || 0;
        break;
      case 'firstName':
        aValue = a.firstName || '';
        bValue = b.firstName || '';
        break;
      case 'lastName':
        aValue = a.lastName || '';
        bValue = b.lastName || '';
        break;
      case 'email':
        aValue = a.email || '';
        bValue = b.email || '';
        break;
      case 'createdAt':
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      default:
        aValue = a[sortBy] || '';
        bValue = b[sortBy] || '';
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Only show full-page loading on initial load, not during search
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full w-12 h-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (currentUser && !isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-xl font-semibold mb-2">Access Denied</div>
          <p className="text-secondary">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">
              {currentUser?.canEdit() ? 'Manage Users' : 'View Users'}
            </h1>
            <p className="text-secondary mt-1">
              {currentUser?.canEdit() ? 'View and manage user accounts' : 'View user accounts'}
            </p>
          </div>
          
          {currentUser?.canEdit() && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-center-content"
              style={{
                marginTop: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add User
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="search-input"
              autoComplete="off"
            />
          </div>


          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="form-input px-4 py-2 rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>



        </div>
        
        {/* Sorting Controls */}
        <div className="sorting-controls">
          {/* Desktop Layout */}
          <div className="desktop-sort">
            <div className="desktop-sort-controls">
              <span className="text-muted">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
                className="form-select-sort"
            >
                          <option value="createdAt">Join Date</option>
            <option value="firstName">First Name</option>
            <option value="lastName">Last Name</option>
            <option value="email">Email</option>
            <option value="concessions">Concessions</option>
            <option value="totalClassesAttended">Total Classes Attended</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="form-button"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>
          </div>
            <span className="text-muted">
            {sortedUsers.length} users found
          </span>
          </div>

          {/* Mobile Layout */}
          <div className="mobile-sort">
            <div className="mobile-sort-header">
              <span className="text-muted">Sort by:</span>
              <span className="text-muted">
                {sortedUsers.length} users found
              </span>
            </div>
            <div className="mobile-sort-controls">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-select-sort mobile-full-width"
              >
                <option value="createdAt">Join Date</option>
                <option value="firstName">First Name</option>
                <option value="lastName">Last Name</option>
                <option value="email">Email</option>
                <option value="concessions">Concessions</option>
                <option value="totalClassesAttended">Total Classes Attended</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="form-button mobile-full-width"
              >
                {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {sortedUsers.length === 0 ? (
        <div className="bg-card rounded-xl shadow-sm border border-primary p-12 text-center">
          <div className="text-secondary">
            <svg className="mx-auto h-12 w-12 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-primary">No users found</h3>
            <p className="mt-1 text-sm text-secondary">
              {searchInput || filterStatus !== 'all' 
                ? 'Try adjusting your search or filters.' 
                : 'Get started by creating a new user.'}
            </p>
            {!searchInput && filterStatus === 'all' && currentUser?.canEdit() && (
              <div className="mt-6">
                <button
                  onClick={() => setShowAddModal(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add User
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-xl shadow-sm border border-primary w-full" style={{ overflow: 'visible' }}>
          <div 
            className="overflow-x-auto p-6"
            style={{
              overflowX: 'auto',
              overflowY: 'visible'
            }}
          >
            <table className="w-full border-collapse" style={{ minWidth: '800px' }}>
              <thead className="bg-hover">
                <tr>
                  <th className="table-header table-header-left">
                    User
                  </th>
                  <th className="table-header table-header-left">
                    Contact
                  </th>
                  <th className="table-header table-header-left">
                    Role & Status
                  </th>
                  <th className="table-header table-header-center">
                    Total Classes Attended
                  </th>
                  <th className="table-header table-header-center">
                    Concessions
                  </th>
                  <th className="table-header table-header-left">
                    Joined
                  </th>
                  {currentUser?.canEdit() && (
                    <th className="table-header table-header-right">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
                          <tbody>
              {sortedUsers.map((user) => (
                <tr key={user._id} className="border-b border-primary hover:bg-hover">
                  <td className={`table-cell table-cell-left ${user.isActive !== false ? 'table-cell-active' : 'table-cell-inactive'}`}>
                    <div className="table-user-info">
                      <div className="table-user-avatar">
                          {user.firstName?.charAt(0)?.toUpperCase() || 'U'}{user.lastName?.charAt(0)?.toUpperCase() || 'S'}
                      </div>
                      <div className="table-user-details">
                        <div className="table-user-name">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="table-user-username">
                          @{user.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  <td className={`table-cell table-cell-left ${user.isActive !== false ? 'table-cell-active' : 'table-cell-inactive'}`}>
                    <div className="table-user-email">{user.email}</div>
                    {user.phone && (
                      <div className="table-user-email">{user.phone}</div>
                    )}
                  </td>
                  
                  <td className={`table-cell table-cell-left ${user.isActive !== false ? 'table-cell-active' : 'table-cell-inactive'}`}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <span className={`badge ${user.role === 'admin' ? 'badge-admin' : user.role === 'coach' ? 'badge-coach' : 'badge-user'}`}>
                          {user.role === 'coach' ? 'Coach' : user.role?.charAt(0)?.toUpperCase() + user.role?.slice(1)?.toLowerCase()}
                      </span>
                        {user.isActive ? (
                          <span className="badge badge-success">
                            Active
                          </span>
                        ) : (
                          <span className="badge badge-error">
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td className={`table-cell table-cell-center ${user.isActive !== false ? 'table-cell-active' : 'table-cell-inactive'}`}>
                    <span className="table-number">
                      {user.totalClassesAttended || 0}
                    </span>
                  </td>
                  
                  <td className={`table-cell table-cell-center ${user.isActive !== false ? 'table-cell-active' : 'table-cell-inactive'}`}>
                    {user.role === 'coach' ? (
                      <span className="text-muted">N/A</span>
                    ) : (
                      (() => {
                        const marks = Array.isArray(user.concessionTypes) ? user.concessionTypes : [];
                        const sharedBalance = user.concessions || 0;
                        const balances = (user.concessionBalances && typeof user.concessionBalances === 'object')
                          ? user.concessionBalances
                          : {};
                        // One row per enrolled purse. Users with no marks keep
                        // the legacy shared-only display.
                        const rows = marks.map((key) => ({
                          key,
                          name: poolDisplayName(concessionTypes, key),
                          value: typeof balances[key] === 'number' ? balances[key] : 0
                        }));
                        if (rows.length === 0) {
                          return (
                            <span className={`table-number ${sharedBalance === 0 ? 'table-number-zero' : sharedBalance < 0 ? 'table-number-negative' : 'table-number-positive'}`}>
                              {sharedBalance}
                            </span>
                          );
                        }
                        return (
                          <div className="flex flex-col gap-1 items-center">
                            {rows.map((row) => (
                              <div key={row.key} className="flex items-center justify-between gap-2 w-full max-w-[120px]">
                                <span className="text-xs text-secondary truncate">{row.name}</span>
                                <span className={`text-sm font-bold ${
                                  row.value < 0 ? 'text-red-500' : row.value === 0 ? 'text-gray-500' : 'text-primary'
                                }`}>
                                  {row.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    )}
                  </td>
                  
                  <td className={`table-cell table-cell-left ${user.isActive !== false ? 'table-cell-active' : 'table-cell-inactive'}`}>
                    <span className="table-date">
                    {formatDate(new Date(user.createdAt), 'MMM dd, yyyy')}
                    </span>
                  </td>
                  
                  {currentUser?.canEdit() && (
                    <td className={`table-cell table-cell-right ${user.isActive !== false ? 'table-cell-active' : 'table-cell-inactive'}`}>
                      <div className="dropdown-container">
                        <button
                          onClick={(e) => handleDropdownToggle(user._id, e.currentTarget)}
                          className="dropdown-button"
                          >
                          <EllipsisVerticalIcon className="w-4 h-4" />
                        </button>
                        
                        {dropdownOpen === user._id && (
                          <div 
                            className="dropdown-menu-table"
                            style={{
                              ...dropdownPosition[user._id]
                          }}>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="dropdown-item border-b border-primary"
                              >
                              <PencilIcon className="w-4 h-4 mr-2" />
                              Edit User
                            </button>
                        <button
                          onClick={() => handleAddToClass(user)}
                          className="dropdown-item"
                          >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add to Class
                        </button>
                            <button
                              onClick={() => handleRemoveFromClass(user)}
                              className="dropdown-item"
                              >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Remove from Class
                            </button>
                            <button
                              onClick={() => handleViewUserHistory(user)}
                              className="dropdown-item"
                              >
                              <EyeIcon className="w-4 h-4 mr-2" />
                              View History
                            </button>
                            <button
                              onClick={() => handleResetPassword(user)}
                              className="dropdown-item"
                              >
                              <ShieldCheckIcon className="w-4 h-4 mr-2" />
                              Reset Password
                            </button>
                            {!user.isSetupComplete && (
                              <button
                                onClick={() => handleResendVerification(user)}
                                className="dropdown-item"
                                >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Resend Verification Link
                              </button>
                            )}
                            <button
                              onClick={() => handleLoginAsUser(user)}
                              className="dropdown-item text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-t border-primary"
                              >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Login as User
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="dropdown-item text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete User
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {totalUsers > 0 && (
          <div className="pagination-container rounded-xl">
            <div className="pagination-mobile">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-mobile-button"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-mobile-button"
              >
                Next
              </button>
            </div>
            <div className="pagination-desktop">
              <div>
                <p className="pagination-info">
                  Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * pageSize, totalUsers)}</span> of{' '}
                  <span className="font-medium">{totalUsers}</span> users
                </p>
              </div>
              <div>
                <nav className="pagination-nav" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-button arrow"
                  >
                    <span className="sr-only">Previous</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`pagination-button page-number ${page === currentPage ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-button arrow"
                  >
                    <span className="sr-only">Next</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>

      {/* Edit User Modal */}
      {showEditModal && currentUser?.canEdit() && (
        <div className="modal-overlay-mobile" onClick={() => setShowEditModal(false)}>
          <div className="modal-container-mobile" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">Edit User</h3>
            </div>
            
            <div className="modal-body-mobile">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', margin: 0 }}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label-small text-secondary">First Name</label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="form-label-small text-secondary">Last Name</label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Username</label>
                  <div className="form-input mt-1 block w-full px-3 py-2 rounded-lg bg-gray-50 text-gray-600 border border-gray-200">
                    @{editForm.username}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Username cannot be changed
                  </p>
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setEditForm({
                        ...editForm, 
                        role: newRole,
                        concessions: newRole === 'coach' ? 0 : editForm.concessions
                      });
                    }}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  >
                    <option value="user">User</option>
                    <option value="coach">Coach</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                </div>
                
                {editForm.role !== 'coach' && concessionTypes.some(t => !t.isShared) && (
                  <div>
                    <label className="form-label-small text-secondary">Class Type Enrolment</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Marked users can see and book classes of these types and receive their concessions.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {concessionTypes.filter(t => !t.isShared).map((t) => {
                        const marks = Array.isArray(editForm.concessionTypes) ? editForm.concessionTypes : [];
                        const checked = marks.includes(t.key);
                        return (
                          <button
                            key={t.key}
                            type="button"
                            onClick={() => toggleUserMark(t.key)}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              checked
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'text-primary border-gray-300 hover:border-primary-400'
                            }`}
                          >
                            {checked ? '✓ ' : ''}{t.displayName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {editForm.role !== 'coach' && (
                  <div>
                    <label className="form-label-small text-secondary">Concessions</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Current balance per wallet. Enter an amount to add (negative to remove), leave blank for no change.
                    </p>
                    <div className="flex flex-col gap-2">
                      {(() => {
                        // Driven by live editForm marks so newly-ticked purses
                        // get their row immediately, before saving.
                        const marks = Array.isArray(editForm.concessionTypes) ? editForm.concessionTypes : [];
                        const balances = (selectedUser?.concessionBalances && typeof selectedUser.concessionBalances === 'object')
                          ? selectedUser.concessionBalances
                          : {};
                        if (marks.length === 0) {
                          return (
                            <p className="text-sm text-muted italic">
                              Not enrolled in any concession type yet - add one above.
                            </p>
                          );
                        }
                        return marks.map((key) => {
                          const value = typeof balances[key] === 'number' ? balances[key] : 0;
                          return (
                            <div key={key} className="flex items-center gap-3">
                              <span className="text-sm text-secondary w-28 truncate" title={poolDisplayName(concessionTypes, key)}>
                                {poolDisplayName(concessionTypes, key)}
                              </span>
                              <span className={`text-sm font-bold w-10 text-right ${value < 0 ? 'text-red-500' : 'text-primary'}`}>
                                {value}
                              </span>
                              <span className="text-secondary">+</span>
                              <input
                                type="number"
                                placeholder="0"
                                value={concessionAdjustments[key] ?? ''}
                                onChange={(e) => setConcessionAdjustments({
                                  ...concessionAdjustments,
                                  [key]: e.target.value
                                })}
                                className="form-input flex-1 px-2 py-1 rounded-lg"
                                disabled={isSavingEdit}
                              />
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="form-label-small text-secondary">Notes</label>
                  <textarea
                    value={editForm.notes || ''}
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    rows={3}
                    placeholder="Add any notes about this user..."
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg resize-vertical"
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                    className="h-5 w-5 mr-3 cursor-pointer"
                  />
                  <label className="text-sm font-medium text-primary">Active Account</label>
                </div>
                </div>
              </div>
              
            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary mr-4"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add to Class Modal */}
      {showAddToClassModal && selectedUserForClass && (
        <div 
          className="modal-overlay-mobile"
          onClick={() => setShowAddToClassModal(false)}
        >
          <div 
            className="modal-container-mobile"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">
                Add {selectedUserForClass.firstName} {selectedUserForClass.lastName} to Class
              </h3>
            </div>
            
            <div className="modal-body-mobile">
              <div className="flex flex-col gap-4 m-0">
                <div>
                  <label className="form-label text-secondary">
                    Select Class
                  </label>
                  <select
                    id="classSelect"
                    className="form-input w-full px-3 py-3 rounded-lg text-sm"
                    onChange={updateButtonText}
                  >
                    {availableClasses.length === 0 ? (
                      <option value="">No matching classes...</option>
                    ) : (
                      <>
                        <option value="">Choose a class...</option>
                        {availableClasses.map((classItem) => {
                          const isFull = classItem.isFull;
                          const availableSpots = classItem.maxCapacity - classItem.currentBookings;
                          const statusText = isFull ? 'FULL - Will add to waitlist' : `${availableSpots} spots available`;
                          const start = new Date(classItem.startTime);
                          const formattedDate = new Intl.DateTimeFormat('en-NZ', {
                            timeZone: 'Pacific/Auckland',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }).format(start);
                          const formattedTime = new Intl.DateTimeFormat('en-NZ', {
                            timeZone: 'Pacific/Auckland',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          }).format(start);
                          
                          return (
                            <option key={classItem._id} value={classItem._id}>
                              {classItem.name} - {formattedDate} at {formattedTime} ({classItem.location}) - {statusText}
                            </option>
                          );
                        })}
                      </>
                    )}
                  </select>
                  {availableClasses.length === 0 && (
                    <p className="text-xs text-muted mt-1">
                      No upcoming classes match this user's tags (e.g. {selectedUserForClass.concessionTypes?.length ? selectedUserForClass.concessionTypes.join(', ') : 'none set'}). Assign the user a tag in their profile to see matching classes.
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label text-secondary">
                    Override Concessions
                  </label>
                  <div className="flex items-center gap-3">
                  <input
                      type="checkbox"
                      id="overrideConcessions"
                      className="h-5 w-5 mr-3 cursor-pointer"
                    />
                    <label htmlFor="overrideConcessions" className="text-sm text-secondary">
                      Allow booking even if user has insufficient concessions
                    </label>
                  </div>
                  <p className="text-xs text-muted mt-1">
                    Current concessions: {selectedUserForClass.concessions}
                  </p>
                </div>
              </div>
                </div>
                
            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
              <button
                onClick={() => {
                  setShowAddToClassModal(false);
                  setSelectedUserForClass(null);
                  setAvailableClasses([]);
                }}
                  className="btn-secondary mr-4"
              >
                Cancel
              </button>
              <button
                onClick={handleAdminBookUser}
                className="btn-primary"
                id="addToClassButton"
                disabled={availableClasses.length === 0}
              >
                Add to Class
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove from Class Modal */}
      {showRemoveFromClassModal && selectedUserForClass && (
        <div 
          className="modal-overlay-mobile"
          onClick={() => {
            setShowRemoveFromClassModal(false);
            setSelectedBookings([]);
            setOverrideConcessionRefund(false);
          }}
        >
          <div 
            className="modal-container-mobile"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">
                Remove {selectedUserForClass.firstName} {selectedUserForClass.lastName} from Class
              </h3>
            </div>
            
            <div className="modal-body-mobile">
              {userBookings.length === 0 ? (
                <div className="text-center p-5">
                  <p className="text-muted text-base">No upcoming bookings found for this user.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-center">
                    <label className="form-label text-secondary">
                      Select Classes to Remove From
                  </label>
                    {selectedBookings.length > 0 && (
                      <span className="text-sm text-primary font-medium">
                        {selectedBookings.length} selected
                      </span>
                    )}
                  </div>
                  {userBookings
                    .sort((a, b) => {
                      // Sort by date: closest to furthest
                      const dateA = new Date(a.displayStartTime || a.classId.startTime);
                      const dateB = new Date(b.displayStartTime || b.classId.startTime);
                      return dateA - dateB;
                    })
                    .map((booking) => {
                    const isSelected = selectedBookings.includes(booking._id);
                    
                    // Calculate if class is within 24 hours
                    const now = new Date();
                    const classStartTime = new Date(booking.displayStartTime || booking.classId.startTime);
                    const timeDifference = classStartTime.getTime() - now.getTime();
                    const hoursDifference = timeDifference / (1000 * 60 * 60);
                    const isWithin24Hours = hoursDifference <= 24;
                    
                    // Determine refund status
                    const willRefundConcessions = !isWithin24Hours || overrideConcessionRefund;
                    const refundStatus = willRefundConcessions 
                      ? (overrideConcessionRefund && isWithin24Hours ? 'Will refund (admin override)' : 'Will refund')
                      : 'No refund (within 24hrs)';
                    const refundColor = willRefundConcessions ? 'text-green-600' : 'refund-warning-text';
                    
                    return (
                      <div 
                        key={booking._id} 
                        className={`booking-option border rounded-lg p-4 cursor-pointer transition-all duration-200 mb-2 ${
                          isSelected 
                            ? 'border-primary bg-primary/10' 
                            : 'border-primary bg-card hover:bg-hover'
                        }`}
                    onClick={(e) => {
                          // Only toggle if clicking on the card, not the checkbox itself
                          if (e.target.type !== 'checkbox') {
                            const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                            checkbox.click();
                          }
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <h4 className="m-0 text-base font-semibold text-primary">
                            {booking.classId.name}
                          </h4>
                            <p className="mt-1 text-sm text-secondary">
                            {new Date(booking.displayStartTime || booking.classId.startTime).toLocaleDateString()} at {(booking.displayStartTime || booking.classId.startTime).split('T')[1].substring(0, 5)}
                          </p>
                            <p className="mt-0.5 text-xs text-muted">
                            {booking.classId.location} • {booking.concessionsUsed} concession{booking.concessionsUsed !== 1 ? 's' : ''} used
                          </p>
                            <p className={`mt-1 text-xs font-medium ${refundColor} flex items-center`}>
                              <TicketIcon className="h-3 w-3 mr-1" />
                              {refundStatus} {booking.concessionsUsed} concession{booking.concessionsUsed !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                setSelectedBookings(prev => [...prev, booking._id]);
                              } else {
                                setSelectedBookings(prev => prev.filter(id => id !== booking._id));
                              }
                            }}
                            className="w-4 h-4 text-primary ml-3"
                        />
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
              
              {/* Admin Override Option */}
              {userBookings.length > 0 && (
                <div className="mt-4 override-rule-container">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="overrideConcessionRefund"
                      checked={overrideConcessionRefund}
                      onChange={(e) => setOverrideConcessionRefund(e.target.checked)}
                      className="w-4 h-4 text-primary mt-0.5"
                    />
                    <div className="flex-1">
                      <label htmlFor="overrideConcessionRefund" className="override-rule-label">
                        Override 24-hour rule
                      </label>
                      <p className="override-rule-description">
                        Check this to refund concessions even for classes within 24 hours. 
                        This overrides the normal policy and will refund all selected bookings.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
              <button
                onClick={() => {
                  setShowRemoveFromClassModal(false);
                  setSelectedUserForClass(null);
                  setUserBookings([]);
                  setSelectedBookings([]);
                  setOverrideConcessionRefund(false);
                }}
                  className="btn-secondary mr-4"
              >
                Cancel
              </button>
              {userBookings.length > 0 && (
                <button
                  onClick={handleAdminRemoveUser}
                  disabled={selectedBookings.length === 0}
                  className={`btn-danger ${selectedBookings.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {selectedBookings.length === 0 
                    ? 'Select Classes to Remove' 
                    : `Remove from ${selectedBookings.length} Class${selectedBookings.length !== 1 ? 'es' : ''}`
                  }
                </button>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && currentUser?.canEdit() && (
        <div className="modal-overlay-mobile" onClick={() => setShowAddModal(false)}>
          <div className="modal-container-mobile" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">Add New User</h3>
            </div>
            
            <div className="modal-body-mobile">
              <div className="flex flex-col gap-4 m-0">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label-small text-secondary">First Name</label>
                    <input
                      type="text"
                      value={addForm.firstName}
                      onChange={(e) => setAddForm({...addForm, firstName: e.target.value})}
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="form-label-small text-secondary">Last Name</label>
                    <input
                      type="text"
                      value={addForm.lastName}
                      onChange={(e) => setAddForm({...addForm, lastName: e.target.value})}
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Email</label>
                  <input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({...addForm, email: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Phone</label>
                  <input
                    type="tel"
                    value={addForm.phone}
                    onChange={(e) => setAddForm({...addForm, phone: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Role</label>
                  <select
                    value={addForm.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setAddForm({
                        ...addForm, 
                        role: newRole,
                        concessions: newRole === 'coach' ? 0 : addForm.concessions
                      });
                    }}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  >
                    <option value="user">User</option>
                    <option value="coach">Coach</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                </div>

                {addForm.role !== 'coach' && concessionTypes.some(t => !t.isShared) && (
                  <div>
                    <label className="form-label-small text-secondary">Class Type Enrolment</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Marked users can see and book classes of these types.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {concessionTypes.filter(t => !t.isShared).map((t) => {
                        const marks = Array.isArray(addForm.concessionTypes) ? addForm.concessionTypes : [];
                        const checked = marks.includes(t.key);
                        return (
                          <button
                            key={t.key}
                            type="button"
                            onClick={() => toggleAddFormMark(t.key)}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              checked
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'text-primary border-gray-300 hover:border-primary-400'
                            }`}
                          >
                            {checked ? '✓ ' : ''}{t.displayName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {addForm.role !== 'coach' && (
                  <div>
                    <label className="form-label-small text-secondary">Concessions</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Starting balance for each wallet. Leave blank for 0.
                    </p>
                    {(() => {
                      const marks = Array.isArray(addForm.concessionTypes) ? addForm.concessionTypes : [];
                      if (marks.length === 0) {
                        return (
                          <p className="text-sm text-muted italic">
                            Not enrolled in any concession type yet - add one above.
                          </p>
                        );
                      }
                      return (
                        <div className="flex flex-col gap-2">
                          {marks.map((key) => {
                            const t = concessionTypes.find(ct => ct.key === key);
                            return (
                              <div key={key} className="flex items-center gap-3">
                                <span className="text-sm text-secondary w-28 truncate" title={t ? t.displayName : key}>
                                  {t ? t.displayName : key}
                                </span>
                                <span className="text-sm font-bold w-10 text-right text-primary">0</span>
                                <span className="text-secondary">+</span>
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={addConcessionAmounts[key] || ''}
                                  onChange={(e) => setAddConcessionAmounts({...addConcessionAmounts, [key]: e.target.value})}
                                  className="form-input flex-1 px-2 py-1 rounded-lg"
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              
                <div>
                  <label className="form-label-small text-secondary">Notes</label>
                  <textarea
                    value={addForm.notes || ''}
                    onChange={(e) => setAddForm({...addForm, notes: e.target.value})}
                    rows={3}
                    placeholder="Add any notes about this user..."
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg resize-vertical"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary mr-4"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddUser}
                  className="btn-primary"
                >
                  Add User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User History Modal */}
      <UserHistory
        user={selectedUserForHistory}
        isOpen={showUserHistoryModal}
        onClose={() => {
          setShowUserHistoryModal(false);
          setSelectedUserForHistory(null);
        }}
      />

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUserForReset && (
        <div className="modal-overlay-mobile" onClick={() => setShowResetPasswordModal(false)}>
          <div className="modal-container-mobile" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">
                Reset Password for {selectedUserForReset.firstName} {selectedUserForReset.lastName}
              </h3>
            </div>
            
            <div className="modal-body-mobile">
              <div className="flex flex-col gap-4 m-0">
                <div>
                  <label className="form-label-small text-secondary">New Password</label>
                  <input
                    type="password"
                    value={resetPasswordForm.newPassword}
                    onChange={(e) => setResetPasswordForm({...resetPasswordForm, newPassword: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    placeholder="Enter new password"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Confirm New Password</label>
                  <input
                    type="password"
                    value={resetPasswordForm.confirmPassword}
                    onChange={(e) => setResetPasswordForm({...resetPasswordForm, confirmPassword: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={resetPasswordForm.sendEmail}
                    onChange={(e) => setResetPasswordForm({...resetPasswordForm, sendEmail: e.target.checked})}
                    className="h-5 w-5 mr-3 cursor-pointer"
                  />
                  <label htmlFor="sendEmail" className="text-sm font-medium text-primary">
                    Send email notification to user
                  </label>
                </div>
              </div>
            </div>
            
            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
                <button
                  onClick={() => setShowResetPasswordModal(false)}
                  className="btn-secondary mr-4"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPasswordSubmit}
                  className="btn-primary"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && selectedUserForDelete && currentUser?.canEdit() && (
        <div className="modal-overlay-mobile" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-container-mobile" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-mobile">
              <h3 className="modal-title text-red-600 text-center">
                Delete User: {selectedUserForDelete.firstName} {selectedUserForDelete.lastName}
              </h3>
            </div>
            
            <div className="modal-body-mobile">
              <div className="text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to delete this user? This action cannot be undone.
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>User:</strong> {selectedUserForDelete.firstName} {selectedUserForDelete.lastName}
                </p>
                <p className="text-sm text-gray-700 mb-4">
                  <strong>Email:</strong> {selectedUserForDelete.email}
                </p>
                <p className="text-xs text-red-600 mb-4">
                  This will also delete all their bookings, waitlist entries, and related data.
                </p>
              </div>
            </div>
            
            <div className="modal-footer-mobile">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary mr-3"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUserSubmit}
                className="btn-danger"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Grant Concessions Modal */}
    </>
  );
};

export default ManageUsers;
